import type { Direction } from '../state/store';

export type CallResult = 'answered' | 'missed' | 'rejected' | 'failed';

export interface CallLogEntry {
  id: string;
  direction: Direction;
  number: string;
  displayName?: string;
  startedAt: number;
  durationSec: number;
  result: CallResult;
}

export type NewCallLogEntry = Omit<CallLogEntry, 'id'>;

export interface CallLog {
  add(entry: NewCallLogEntry): CallLogEntry;
  list(): CallLogEntry[];
  missedSinceLastView(): number;
  markViewed(): void;
  clear(): void;
}

export const CALLLOG_KEY = 'webtelefon.calllog.v1';

interface Persisted {
  entries: CallLogEntry[];
  lastViewedAt: number;
}

const RESULTS: ReadonlySet<unknown> = new Set<CallResult>(['answered', 'missed', 'rejected', 'failed']);
const DIRECTIONS: ReadonlySet<unknown> = new Set<Direction>(['in', 'out']);

function isEntry(e: unknown): e is CallLogEntry {
  if (typeof e !== 'object' || e === null) return false;
  const o = e as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.number === 'string' &&
    typeof o.startedAt === 'number' &&
    typeof o.durationSec === 'number' &&
    RESULTS.has(o.result) &&
    DIRECTIONS.has(o.direction) &&
    (o.displayName === undefined || typeof o.displayName === 'string')
  );
}

function read(storage: Storage | null): Persisted {
  const empty: Persisted = { entries: [], lastViewedAt: 0 };
  if (!storage) return empty;
  try {
    const raw = storage.getItem(CALLLOG_KEY);
    if (!raw) return empty;
    const data = JSON.parse(raw) as Partial<Persisted>;
    return {
      entries: Array.isArray(data.entries) ? data.entries.filter(isEntry) : [],
      lastViewedAt: typeof data.lastViewedAt === 'number' ? data.lastViewedAt : 0,
    };
  } catch {
    return empty;
  }
}

export function createCallLog(
  storage: Storage | null,
  opts: { max?: number; now?: () => number } = {},
): CallLog {
  const max = opts.max ?? 200;
  const now = opts.now ?? Date.now;
  let data = read(storage);
  let counter = 0;

  const write = () => {
    if (!storage) return;
    try {
      storage.setItem(CALLLOG_KEY, JSON.stringify(data));
    } catch {
      // Storage voll oder gesperrt: nur im Speicher weiterarbeiten.
    }
  };

  return {
    add(entry) {
      const full: CallLogEntry = { ...entry, id: `${entry.startedAt}-${now()}-${counter++}` };
      data = { ...data, entries: [full, ...data.entries].slice(0, max) };
      write();
      return full;
    },
    list: () => [...data.entries],
    missedSinceLastView: () =>
      data.entries.filter((e) => e.result === 'missed' && e.startedAt > data.lastViewedAt).length,
    markViewed() {
      data = { ...data, lastViewedAt: now() };
      write();
    },
    clear() {
      data = { entries: [], lastViewedAt: 0 };
      write();
    },
  };
}
